/**
 * The memory access model in one place, for the Access view: who stands at which level, who holds
 * which compartment, and what follows from a change, counted over the organisation's memory as the
 * signed-in administrator can read it. One rule decides everything: a person reads an item when its
 * level is at or below their clearance and they hold every compartment on it (`mayRead`).
 */

import { DEFAULT_CLEARANCE, mayRead, SENSITIVITIES, SENSITIVITY_RANK, type Clearance, type Compartment, type CompartmentGrant, type Entitlement, type MemoryItem, type Sensitivity } from '@aioe/shared';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';

export const LEVEL_TEXT: Record<Sensitivity, string> = { public: 'Public', internal: 'Internal', confidential: 'Confidential', restricted: 'Restricted' };
/** One plain sentence per level: what belongs there. */
export const LEVEL_MEANS: Record<Sensitivity, string> = {
  public: 'Fine for anyone, inside the organisation or out.',
  internal: 'For people in the organisation. Everyone reads it unless set lower.',
  confidential: 'For the people who need it. Anything no rule labels lands here.',
  restricted: 'The most sensitive. As few people as possible.',
};

export type CompartmentRow = Compartment & { grants: CompartmentGrant[] };
export type Subject = { type: 'user' | 'group' | 'project'; id: string };

export interface AccessModel {
  compartments: CompartmentRow[];
  clearances: Clearance[];
  /** The organisation's memory as the signed-in administrator reads it. */
  items: MemoryItem[];
  /** Items the administrator cannot read themselves: counts leave them out. */
  hiddenFromMe: number;
  me?: Entitlement;
  projects: Map<string, string>;
}

/** Everything the Access view reads, loaded together. Undefined until the reader is known to be an administrator. */
export function useAccessModel(enabled: boolean) {
  const load = useLoad(async (): Promise<AccessModel | undefined> => {
    if (!enabled) return undefined;
    const [compartments, clearances, graph, me, projects] = await Promise.all([
      routes.compartments(),
      routes.clearances(),
      routes.orgMemoryGraph().catch(() => ({ items: [] as MemoryItem[], itemsFiltered: 0 })),
      routes.orgEntitlement().catch(() => undefined),
      routes.projects().catch(() => []),
    ]);
    return { compartments, clearances, items: graph.items.filter((i) => !i.deletedAt), hiddenFromMe: graph.itemsFiltered, me, projects: new Map(projects.map((p) => [p.id, p.name])) };
  }, [enabled]);
  return load;
}

export const itemName = (i: MemoryItem): string => {
  const d = i.data as Record<string, unknown>;
  return (typeof d.title === 'string' && d.title) || (typeof d.name === 'string' && d.name) || i.id;
};

export const compartmentName = (m: AccessModel, id: string): string => m.compartments.find((c) => c.id === id)?.name ?? id;

export const levelBelow = (s: Sensitivity): Sensitivity | undefined => SENSITIVITIES[SENSITIVITY_RANK[s] - 1];
export const levelAbove = (s: Sensitivity): Sensitivity | undefined => SENSITIVITIES[SENSITIVITY_RANK[s] + 1];
export const atLeast = (a: Sensitivity, b: Sensitivity): boolean => SENSITIVITY_RANK[a] >= SENSITIVITY_RANK[b];

/** What a reader with this entitlement reads of `items`, and why the rest is hidden from them. */
export function reachOf(e: Pick<Entitlement, 'clearance' | 'compartments'>, items: MemoryItem[]) {
  const reads: MemoryItem[] = [];
  const above = new Map<Sensitivity, number>();
  const missing = new Map<string, number>();
  for (const i of items) {
    if (mayRead(e, i.label)) {
      reads.push(i);
      continue;
    }
    if (!atLeast(e.clearance, i.label.sensitivity)) above.set(i.label.sensitivity, (above.get(i.label.sensitivity) ?? 0) + 1);
    else for (const c of i.label.compartments) if (!e.compartments.includes(c)) missing.set(c, (missing.get(c) ?? 0) + 1);
  }
  return { reads, above, missing };
}

/** Where a person's clearance comes from: set for them, from one of their groups, or the default. */
export function clearanceSource(m: AccessModel, userId: string, e: Entitlement): 'own' | 'group' | 'default' {
  if (m.clearances.some((c) => c.subjectType === 'user' && c.subjectId === userId)) return 'own';
  return (e.organisationClearance ?? e.clearance) === DEFAULT_CLEARANCE ? 'default' : 'group';
}

/** Whether a person holds a compartment by a grant to them, rather than through a group or a project. */
export const holdsDirectly = (m: AccessModel, userId: string, compartmentId: string): boolean =>
  Boolean(m.compartments.find((c) => c.id === compartmentId)?.grants.some((g) => g.subjectType === 'user' && g.subjectId === userId));

/** The people the directory of grants and clearances names, plus the signed-in person: the ones whose reach can be counted. */
export function namedPeople(m: AccessModel): string[] {
  const ids = new Set<string>();
  if (m.me) ids.add(m.me.act);
  for (const c of m.clearances) if (c.subjectType === 'user') ids.add(c.subjectId);
  for (const c of m.compartments) for (const g of c.grants) if (g.subjectType === 'user') ids.add(g.subjectId);
  return [...ids].slice(0, 60);
}

/** Each named person's entitlement, as AIOE would mint it (groups and project memberships resolved on the server). */
export function usePeopleReach(m: AccessModel | undefined) {
  const people = m ? namedPeople(m) : [];
  return useLoad(async () => {
    const out = new Map<string, Entitlement>();
    await Promise.all(
      people.map((id) =>
        routes
          .orgEntitlement(id)
          .then((e) => void out.set(id, e))
          .catch(() => undefined),
      ),
    );
    return out;
  }, [people.join(','), m?.clearances, m?.compartments]);
}

/** A clearance list with one subject moved to `level`, or back to the default when `level` is undefined. */
export function withClearance(list: Clearance[], s: { type: 'user' | 'group'; id: string }, level: Sensitivity | undefined): Clearance[] {
  const rest = list.filter((c) => !(c.subjectType === s.type && c.subjectId === s.id));
  return level ? [...rest, { subjectType: s.type, subjectId: s.id, level }] : rest;
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** "the derived id" for a new compartment: lower case, safe, unique among the ones there are. */
export function compartmentIdFor(name: string, taken: string[]): string {
  const base =
    name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9:_.-]+/g, '-')
      .replace(/^[^a-z0-9]+|-+$/g, '')
      .slice(0, 90) || 'compartment';
  let id = base;
  for (let n = 2; taken.includes(id); n++) id = `${base}-${n}`;
  return id;
}
